<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue';
import BaseButton from './BaseButton.vue';
import { Locales } from '@/store/language.interface';
import { getLabel } from '@/utils/data-display-utils';

const userComment = ref<string>('');
const textareaRef = ref<HTMLTextAreaElement | null>(null);

// PROPS
const props = defineProps<{
  languageEnabled?: Locales[];
}>();

const emit = defineEmits<{
  (e: 'submitComment', userComment: string): void;
  (e: 'startComment'): void;
}>();

function submitComment() {
  emit('submitComment', userComment.value);
  userComment.value = '';
}

function startComment() {
  emit('startComment');
}

// Adjust the textarea height dynamically up to a maximum height (5 lines)
function adjustHeight() {
  if (textareaRef.value) {
    const textarea = textareaRef.value;
    // Reset height to calculate the new height
    textarea.style.height = 'auto';
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);
    // Calculate max height based on 5 lines of text
    const maxHeight = lineHeight * 5 + paddingTop + paddingBottom;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = newHeight + 'px';
    // Show scrollbar if content exceeds max height, otherwise hide it
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }
}

// Adjust height on component mount in case there is pre-filled content
onMounted(() => {
  nextTick(() => {
    adjustHeight();
  });
});
</script>

<template>
  <div class="base-form__comments-input">
    <textarea
      ref="textareaRef"
      v-model="userComment"
      name="comment"
      :placeholder="getLabel('form.comment-placeholder', props.languageEnabled)"
      rows="1"
      wrap="soft"
      class="comment-textarea"
      @input="adjustHeight"
      @focus="startComment"
    ></textarea>
    <BaseButton
      class="action"
      icon="o_send"
      :ariaLabel="getLabel('buttons.submit', props.languageEnabled)"
      :title="getLabel('buttons.submit', props.languageEnabled)"
      @click="submitComment"
    />
  </div>
</template>

<style lang="scss">
.base-form__comments-input {
  display: flex;
  flex-direction: row;
  gap: 5px;
  width: 100%;

  .comment-textarea {
    width: 100%;
    resize: none;
    overflow-y: hidden; // Initially hide vertical scrollbar
    white-space: pre-wrap; // Preserve newlines and wrap text
    padding: 8px;
    font-size: 1rem;
    line-height: 1.5;
    border: 1px solid #ccc;
    border-radius: 4px;
    transition: height 0.2s;
  }

  .action {
    align-self: flex-end;
    height: 40px;
  }
}
</style>
